/// <amd-module name="VSS/Features/Markdown/Plugins/TableOfContents" />
import * as MarkdownIt from "markdown-it";
import * as Resources from "VSS/Features/Markdown/Resources";

import { IState } from "../MarkdownIt";

export interface ITableOfContentsOptions {
    includeLevel?: number[];
    containerClass?: string;
    slugify?: (str: string) => string;
    markerPattern?: RegExp;
    listType?: string;
    format?: (str: string) => string;
    forceFullToc?: boolean;
    containerHeaderHtml?: string;
    containerFooterHtml?: string;
    transformLink?(str: string): string;
}

/**
 * Based on markdown-it-table-of-contents . Version used in VSTS : 0.3.6
 * Task 1420540: Enable AMD support in the table-of contents plugins to consume them through the npm packages
 **/
export class TableOfContents {
    private _options: ITableOfContentsOptions;
    private _gState: IState;

    constructor(md: MarkdownIt.MarkdownIt, options?: ITableOfContentsOptions) {
        const defaults: ITableOfContentsOptions = {
            includeLevel: [1, 2],
            containerClass: "table-of-contents",
            slugify: (str: string) => {
                return encodeURIComponent(
                    str
                        .trim()
                        .toLowerCase()
                        .replace(/\s+/g, "-")
                );
            },
            markerPattern: /^\[\[toc\]\]/im,
            listType: "ul"
        };

        this._options = { ...defaults, ...options };

        // Adding rules to the markdown renderer for tags with names of properties (ex. toc_open).
        md.renderer.rules.toc_open = this._tocOpenHandler;
        md.renderer.rules.toc_close = this._tocCloseHandler;
        md.renderer.rules.toc_body = this._tocBodyHandler;

        // Catch all the tokens for iteration later
        md.core.ruler.push("grab_state", (state: IState) => {
            this._gState = state;
        });

        // Insert TOC rule
        md.inline.ruler.after("emphasis", "toc", this._tocHandler);
    }

    private _tocHandler = (state: IState, silent: boolean): boolean => {
        let token;
        let match;

        // Reject if the token does not start with [
        if (state.src.charCodeAt(state.pos) !== 0x5b /* [ */) {
            return false;
        }
        // Don't run any pairs in validation mode
        if (silent) {
            return false;
        }

        // Find TOC markdown tag and return if none was found
        if (this._options.markerPattern) {
            match = this._options.markerPattern.exec(state.src);
        }
        match = !match
            ? []
            : match.filter(function(m) {
                  return m;
              });
        if (match.length < 1) {
            return false;
        }

        // Check the index of match is same as pos
        // This we do because we push tokens as state.pos only, so the match has to be in that index
        if (state.pos !== state.src.indexOf(match[0])) {
            return false;
        }

        // Build content. `state.push` adds new token to "stream". Arguments: type, tag, nesting.
        // For more see Token in lib documentation.
        token = state.push("toc_open", "toc", 1);
        token.markup = "[[toc]]";
        token = state.push("toc_body", "", 0);
        token = state.push("toc_close", "toc", -1);

        // Update pos so the parser can continue
        const newline = state.src.indexOf("\n", state.pos);
        if (newline !== -1) {
            state.pos = newline;
        } else {
            state.pos = state.pos + state.posMax + 1;
        }

        return true;
    };

    private _tocOpenHandler = (tokens: MarkdownIt.Token[], index: number): string => {
        let tocOpenHtml = `<div class="${this._options.containerClass}" aria-label="${Resources.MarkdownTocAriaLabel}" role="${"navigation"}">`;

        if (this._options.containerHeaderHtml) {
            tocOpenHtml += this._options.containerHeaderHtml;
        }

        return tocOpenHtml;
    };

    private _tocCloseHandler = (tokens: MarkdownIt.Token[], index: number): string => {
        let tocFooterHtml = "";

        if (this._options.containerFooterHtml) {
            tocFooterHtml = this._options.containerFooterHtml;
        }

        return tocFooterHtml + "</div>";
    };

    private _tocBodyHandler = (tokens: MarkdownIt.Token[], index: number) => {
        if (this._options.forceFullToc) {
            /*

            Renders full TOC even if the hierarchy of headers contains
            a header greater than the first appearing header

            ## heading 2
            ### heading 3
            # heading 1

            Result TOC:
            - heading 2
               - heading 3
            - heading 1

            */
            let tocBody = "";
            let pos: any = 0;
            const tokenLength = this._gState && this._gState.tokens && this._gState.tokens.length;

            // Finding all headers. Cause _renderChildsTokens will return if greater header is below,
            // so we just continue token handling in this 'while'.
            while (pos < tokenLength) {
                const tocHierarchy = this._renderChildsTokens(pos, this._gState.tokens);
                pos = tocHierarchy[0];
                tocBody += tocHierarchy[1];
            }

            return tocBody;
        } else {
            return this._renderChildsTokens(0, this._gState.tokens)[1];
        }
    };

    /**
     * Finding headers, add them to as list item and return the whole list. Works recursively for subheaders.
     * @param pos position at the tokens array since we iterating on it.
     * @param tokens all tokens at the page.
     */
    private _renderChildsTokens = (pos: any, tokens: MarkdownIt.Token[]) => {
        let headings = [],
            buffer = "",
            currentLevel: any,
            subHeadings: any,
            size = tokens.length,
            i = pos;

        while (i < size) {
            const token = tokens[i];
            const heading = tokens[i - 1];
            const level = token.tag ? parseInt(token.tag.substr(1, 1)) : -1; // Getting html tag
            // For more info on tokens type see lib documentation.
            if (token.type !== "heading_close" || this._options.includeLevel!.indexOf(level) === -1 || heading.type !== "inline") {
                i++;
                continue; // Skip if not matching criteria
            }
            // If first level we just init it
            if (!currentLevel) {
                currentLevel = level; // We init with the first found level
            } else {
                if (level > currentLevel) {
                    // If we need to go deeper we render children and return them
                    subHeadings = this._renderChildsTokens(i, tokens);
                    // Children tokens
                    buffer += subHeadings[1];
                    // update position
                    i = subHeadings[0];
                    // Skipping further handling since we need to firstly feel deeper levels.
                    continue;
                }
                if (level < currentLevel) {
                    // If it was last level we just close our built list and return it.
                    // Finishing the sub headings
                    buffer += "</li>";
                    headings.push(buffer);
                    return [i, "<" + this._options.listType + ">" + headings.join("") + "</" + this._options.listType + ">"];
                }
                if (level === currentLevel) {
                    // If it is current level we close previous heading line before starting this one
                    // Finishing the sub headings
                    buffer += "</li>";
                    headings.push(buffer);
                }
            }
            // Creating link to the header
            const slugifiedContent = this._options.slugify!(heading.content);
            let link = "#" + slugifiedContent;
            // Transform it if needed
            if (this._options.transformLink) {
                link = this._options.transformLink(link);
            }
            // Creating line for the header in ToC
            buffer = `<li><a href="${link}">`;
            buffer += typeof this._options.format === "function" ? this._options.format(heading.content) : heading.content;
            buffer += "</a>";
            i++;
        }
        // Close our list if it not empty
        buffer += buffer === "" ? "" : "</li>";
        headings.push(buffer);

        // Return everything
        return [i, "<" + this._options.listType + ">" + headings.join("") + "</" + this._options.listType + ">"];
    };
}
